import { useCallback, useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { firstValueFrom } from 'rxjs';

import { useResourceBookingServiceClient } from 'hooks/use-resource-booking-service-client.hook';
import { CoworkerResponse } from 'store/api-client/coworker-types';

// Bookings change through the day, but a search result a minute old is still a good answer while
// the user types; keying by query keeps distinct searches apart.
const STALE_TIME = 60 * 1000;

// The BE caps a page at 100; larger directories are paged through with `loadMore`.
const PAGE_SIZE = 100;

const EMPTY: CoworkerResponse[] = [];

export const COWORKER_SEARCH_QUERY_KEY = 'coworker-search';

interface CoworkerSearchPage {
  coworkers: CoworkerResponse[];
  total: number;
}

const loadedCount = (pages: CoworkerSearchPage[]): number =>
  pages.reduce((count, page) => count + page.coworkers.length, 0);

/**
 * A row nobody can recognise — blank name AND blank initials (a users-service profile with no name
 * at all) — is not shown. The BE is meant to leave such users out of the directory; this keeps an
 * older BE from putting empty rows on screen. `total` stays the BE's and may count them.
 *
 * Optional chaining on purpose: the wire type says string, but a response that omits or nulls a
 * field must drop the row, not throw out of the memo.
 */
const hasIdentity = ({ name, initials }: CoworkerResponse): boolean => !!(name?.trim() || initials?.trim());

interface UseCoworkerSearchQueryParams {
  /** Already debounced by the caller. Blank returns the whole visible directory. */
  query: string;
  /** Day whose booking each row reports, YYYY-MM-DD. */
  date: string;
  enabled?: boolean;
}

export interface UseCoworkerSearchQueryResult {
  /** Every row loaded so far, in the BE's order — all pages flattened. */
  coworkers: CoworkerResponse[];
  /** Matches across ALL pages, as reported by the BE; `coworkers.length` is how many are loaded. */
  total: number;
  /** More matches exist past the loaded rows; `loadMore` fetches the next page of them. */
  hasMore: boolean;
  loadMore: VoidFunction;
  isLoadingMore: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: VoidFunction;
}

/**
 * The find-a-coworker sheet's rows: `GET buildings/:uuid/coworkers` on resource-booking-service,
 * which applies the visibility predicate and the per-day booking join server-side. Paged by
 * offset: the first page arrives with the search, further pages on demand, so a directory larger
 * than one page is reachable and the caller can tell loaded rows from the full match count. The
 * previous result is kept on screen while the next query loads, so the list does not blink on
 * every keystroke.
 */
export const useCoworkerSearchQuery = ({
  query,
  date,
  enabled = true,
}: UseCoworkerSearchQueryParams): UseCoworkerSearchQueryResult => {
  const { getClient, buildingUuid } = useResourceBookingServiceClient();

  const isEnabled = enabled && !!buildingUuid;

  const result = useInfiniteQuery({
    queryKey: [COWORKER_SEARCH_QUERY_KEY, buildingUuid, date, query],
    queryFn: ({ pageParam }) =>
      firstValueFrom(
        getClient().searchCoworkers(buildingUuid as string, { query, date, limit: PAGE_SIZE, offset: pageParam }),
      ),
    initialPageParam: 0,
    // The next offset is simply how many rows are loaded; an empty page ends paging even if the
    // BE's total drifted upwards meanwhile, so a stale total can never loop.
    getNextPageParam: (lastPage: CoworkerSearchPage, pages: CoworkerSearchPage[]) => {
      const loaded = loadedCount(pages);

      return lastPage.coworkers.length > 0 && loaded < lastPage.total ? loaded : undefined;
    },
    enabled: isEnabled,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });

  const pages = result.data?.pages;
  const coworkers = useMemo(() => pages?.flatMap(page => page.coworkers).filter(hasIdentity) ?? EMPTY, [pages]);
  const { fetchNextPage, isFetchingNextPage } = result;
  const loadMore = useCallback(() => {
    if (!isFetchingNextPage) {
      // Fire-and-forget: the result surfaces through `coworkers` / `isError`, not this promise.
      fetchNextPage().catch(() => undefined);
    }
  }, [fetchNextPage, isFetchingNextPage]);

  return {
    coworkers,
    total: pages?.[pages.length - 1]?.total ?? 0,
    hasMore: result.hasNextPage,
    loadMore,
    isLoadingMore: isFetchingNextPage,
    isLoading: isEnabled && result.isPending,
    isError: result.isError,
    refetch: result.refetch,
  };
};
